import React, { useState, useEffect } from 'react';
import { X, ChevronRight, ChevronLeft } from 'lucide-react';

export type TourStep = {
  targetId: string;
  title: string;
  content: string;
  position?: 'top' | 'bottom' | 'left' | 'right';
};

interface GuidedTourProps {
  steps: TourStep[];
  isOpen: boolean;
  onClose: () => void;
}

export default function GuidedTour({ steps, isOpen, onClose }: GuidedTourProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const [targetRect, setTargetRect] = useState<DOMRect | null>(null);

  useEffect(() => {
    if (!isOpen) return;

    const updatePosition = () => {
      const el = document.getElementById(steps[currentStep].targetId);
      if (el) {
        setTargetRect(el.getBoundingClientRect());
        // Scroll element into view if needed
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else {
        setTargetRect(null);
      }
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    return () => window.removeEventListener('resize', updatePosition);
  }, [currentStep, isOpen, steps]);

  if (!isOpen || !targetRect) return null;

  const step = steps[currentStep];
  
  // Calculate position based on targetRect and step.position
  const popoverStyle: React.CSSProperties = {
    position: 'fixed',
    zIndex: 9999,
    width: '300px',
    background: 'rgba(5,9,18,0.95)',
    border: '1px solid var(--color-primary)',
    borderRadius: '12px',
    padding: '1.25rem',
    boxShadow: '0 10px 40px rgba(0,0,0,0.5), 0 0 20px rgba(0,255,136,0.2)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    backdropFilter: 'blur(10px)',
  };

  // Simple positioning logic (defaults to center of screen if position is complex)
  let top = targetRect.bottom + 16;
  let left = targetRect.left;

  if (step.position === 'right') {
      top = targetRect.top;
      left = targetRect.right + 16;
  } else if (step.position === 'left') {
      top = targetRect.top;
      left = targetRect.left - 316;
  }

  // Boundary checks (very basic)
  if (left + 300 > window.innerWidth) left = window.innerWidth - 320;
  if (top + 150 > window.innerHeight) top = targetRect.top - 160;

  popoverStyle.top = top;
  popoverStyle.left = Math.max(16, left);

  return (
    <>
      {/* Highlight overlay */}
      <div style={{
        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
        background: 'rgba(0,0,0,0.5)', zIndex: 9998,
        clipPath: `polygon(
          0% 0%, 0% 100%, ${targetRect.left - 8}px 100%, 
          ${targetRect.left - 8}px ${targetRect.top - 8}px,
          ${targetRect.right + 8}px ${targetRect.top - 8}px,
          ${targetRect.right + 8}px ${targetRect.bottom + 8}px,
          ${targetRect.left - 8}px ${targetRect.bottom + 8}px,
          ${targetRect.left - 8}px 100%, 100% 100%, 100% 0%
        )`
      }} />

      {/* Target Pulsing border */}
      <div style={{
          position: 'fixed',
          top: targetRect.top - 8, left: targetRect.left - 8,
          width: targetRect.width + 16, height: targetRect.height + 16,
          border: '2px solid var(--color-primary)',
          borderRadius: '8px',
          zIndex: 9998,
          pointerEvents: 'none',
          animation: 'pulse 2s infinite'
      }} />

      {/* Popover */}
      <div style={popoverStyle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
          <h3 style={{ margin: 0, color: 'var(--text-primary)', fontSize: '1.1rem', fontWeight: 700 }}>{step.title}</h3>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer' }}>
            <X size={16} />
          </button>
        </div>
        <p style={{ color: 'var(--text-secondary)', fontSize: '0.85rem', lineHeight: 1.5, marginBottom: '1.5rem' }}>
          {step.content}
        </p>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: '0.25rem' }}>
            {steps.map((_, i) => (
              <div key={i} style={{ 
                width: '6px', height: '6px', borderRadius: '50%', 
                background: i === currentStep ? 'var(--color-primary)' : 'var(--border-color)' 
              }} />
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            {currentStep > 0 && (
              <button 
                onClick={() => setCurrentStep(prev => prev - 1)}
                style={{ background: 'var(--bg-surface)', border: '1px solid var(--border-color)', color: 'var(--text-primary)', borderRadius: '6px', padding: '0.4rem 0.5rem', cursor: 'pointer' }}
              >
                <ChevronLeft size={16} />
              </button>
            )}
            <button 
              onClick={() => {
                if (currentStep < steps.length - 1) {
                  setCurrentStep(prev => prev + 1);
                } else {
                  onClose();
                }
              }}
              style={{ display: 'flex', alignItems: 'center', gap: '0.25rem', background: 'var(--color-primary)', border: 'none', color: '#000', borderRadius: '6px', padding: '0.4rem 0.75rem', fontWeight: 700, cursor: 'pointer' }}
            >
              {currentStep < steps.length - 1 ? (
                <>Next <ChevronRight size={16} /></>
              ) : (
                'Get Started'
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
